import { InputRule, Node } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-2'
import NodeViewVariableMention from './NodeViewVariableMention.vue'

export const EXTENSION_KEY = 'variableMention'

export const VariableMention = Node.create({
  name: EXTENSION_KEY,
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true, // Makes it a single unit that can't be edited internally

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => ({ 'data-id': attributes.id }),
      },
      label: {
        default: null,
        parseHTML: element => element.getAttribute('data-label'),
        renderHTML: attributes => ({ 'data-label': attributes.label }),
      },
      value: {
        default: null,
        parseHTML: element => element.getAttribute('data-value'),
        renderHTML: attributes => ({ 'data-value': attributes.value }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="variable-mention"]',
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', {
      'data-type': 'variable-mention',
      'class': 'editor-variable-mention',
      ...HTMLAttributes,
    }, node.attrs.label]
  },

  addNodeView() {
    return VueNodeViewRenderer(NodeViewVariableMention)
  },

  addCommands() {
    return {
      insertVariableMention: attributes => ({ chain }) => {
        if (!attributes || !attributes.id || !attributes.label || !attributes.value) {
          console.error('Cannot insert variable mention without id, label, and value attributes.')
          return false
        }
        return chain().insertContent({
          type: this.name, // Use the node's name ('variableMention')
          attrs: attributes,
        }).run()
      },
      removeVariableMention: mentionId => ({ tr, state, dispatch }) => {
        const { doc } = state
        let mentionFound = false
        let fromPos = -1
        let toPos = -1

        // Find the mention node by its ID
        doc.descendants((node, pos) => {
          if (node.type.name === this.name && node.attrs.id === mentionId) {
            fromPos = pos
            toPos = pos + node.nodeSize
            mentionFound = true
            return false // Stop searching once found
          }
          // Continue searching if not found yet
          return true
        })

        // If found, delete the node
        if (mentionFound && fromPos !== -1 && toPos !== -1 && dispatch) {
          tr.delete(fromPos, toPos)
          dispatch(tr)
          return true
        }

        return false
      },
    }
  },

  addStorage() {
    return {
      markdown: {
        // How to serialize this node to Markdown: {{id}}
        serialize: (state, node) => {
          const { id } = node.attrs // id, label, and value are the same
          if (!id) {
            console.warn('Skipping mention serialization: Missing id.', node.attrs)
            // Optionally, write nothing or some fallback text.
            return
          }
          state.write(`{{${id}}}`)
        },
        // How to parse this node from Markdown
        parse: {
          // This is run when markdown-it is initialized
          setup(markdownit) {
            // Renderer rule for the opening tag
            // Creates the <span data-type="variable-mention" ...> tag
            markdownit.renderer.rules.variable_mention_open = (tokens, idx) => {
              const token = tokens[idx]
              const id = token.attrGet('id')
              // label and value are the same as id as per the new logic
              const label = token.attrGet('label')
              const value = token.attrGet('value')

              let attrs = `data-type="variable-mention" data-id="${id}" data-label="${label}" data-value="${value}"`
              attrs += ` class="editor-variable-mention"`
              return `<span ${attrs}>`
            }

            // Renderer rule for the closing tag
            markdownit.renderer.rules.variable_mention_close = () => {
              return '</span>'
            }

            // Inline ruler rule to detect the mention syntax {{id}}
            markdownit.inline.ruler.before('link', 'variable_mention_curly', (state, silent) => {
              const startPos = state.pos
              const max = state.posMax

              // Check for {{
              if (startPos + 1 >= max || state.src.charCodeAt(startPos) !== 0x7B /* { */ || state.src.charCodeAt(startPos + 1) !== 0x7B /* { */) {
                return false
              }

              let pos = startPos + 2 // Start after {{
              let contentEnd = -1

              // Find }}
              while (pos < max) {
                if (pos + 1 < max && state.src.charCodeAt(pos) === 0x7D /* } */ && state.src.charCodeAt(pos + 1) === 0x7D /* } */) {
                  contentEnd = pos
                  break
                }
                // Disallow nested {{ within the content for simplicity
                if (pos + 1 < max && state.src.charCodeAt(pos) === 0x7B && state.src.charCodeAt(pos + 1) === 0x7B) {
                  return false
                }
                pos++
              }

              if (contentEnd === -1) { // No closing }} found
                return false
              }

              const id_content = state.src.slice(startPos + 2, contentEnd)

              // Ensure content is not empty and does not contain newlines
              const actualId = id_content.trim()
              if (actualId === '' || id_content.includes('\n')) {
                return false
              }

              if (silent) {
                // In silent mode, we only care if it matches, advance position and return true
                state.pos = contentEnd + 2 // Advance past }}
                return true
              }

              // --- Token Pushing ---
              const tokenOpen = state.push('variable_mention_open', 'span', 1)
              tokenOpen.attrs = [
                ['id', actualId],
                ['label', actualId], // Label is the same as ID
                ['value', actualId], // Value is the same as ID
              ]

              const tokenText = state.push('text', '', 0)
              // The text content for the node.
              // node.attrs.label (which is actualId) will be used by renderHTML's fallback.
              // The NodeViewVariableMention.vue component can use node.attrs.id and display it as {{id}}.
              tokenText.content = actualId

              state.push('variable_mention_close', 'span', -1)
              // --- End of Token Pushing ---

              // Advance the parser position past the entire matched sequence (including '}}')
              state.pos = contentEnd + 2
              return true
            })
          },
        },
      },
    }
  },

  addInputRules() {
    return [
      new InputRule({
        find: /\{\{\s*([\w.-]+)\s*\}\}$/, // Regex to match {{id}} at the end of input
        handler: ({ state, range, match }) => {
          const { tr } = state
          const { from, to } = range
          const actualId = match[1] // The captured ID

          if (actualId) {
            // Replace the typed text with the VariableMention node
            tr.replaceRangeWith(
              from,
              to,
              this.type.create({ id: actualId, label: actualId, value: actualId }),
            )
          }

          return tr
        },
      }),
    ]
  },
})
